// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// OS REQUISITOS ESCOLHEM-SE DE UMA LISTA — e o que vem do email casa-se com ela
// ============================================================================
//
// 📗 Cliente, 2026-10-22: *«os requisitos têm de ser selecionados de uma lista se forem
//    detetados, não podem ser através de texto»*. Os ecrãs manuais (Novo pedido, Editar pedido)
//    já escolhiam de uma grelha desde 2026-08-25; a ficha do email gravava a frase do modelo
//    («guia acompanhante, que precisa de microfone; autocarro com acesso fácil…») em
//    `pedido.requisitos`, e o F2 tinha de a adivinhar.
//
// ⚠️ **A LISTA MUDOU DE CASA, NÃO DE CONTEÚDO.** Vivia em `apps/web/src/mapa/requisitos-pedido.ts`;
//    passa a viver aqui porque agora a API também precisa dela (a gravação do pedido do email
//    guarda só itens da lista). Duas cópias divergiam — a web reexporta esta.
//
// ⚠️ **O RECONHECIMENTO É O DO MOTOR** (`SINONIMOS_CONHECIDOS`, o mesmo do F2): uma segunda tabela
//    de sinónimos para a mesma pergunta («que característica é esta frase?») divergiria. A única
//    coisa a mais é procurar a FRASE-CHAVE dentro de uma frase longa («que precisa de microfone»),
//    pela chave mais longa primeiro — o modelo copia as frases do cliente tal e qual.
// ============================================================================
import { normalizarRequisito, normalizarTexto, SINONIMOS_CONHECIDOS, separarRequisitos, tabelaDeSinonimos } from './requisitos.js';
export const REQUISITOS_DA_VIATURA = [
    { rotulo: 'Wi-Fi', texto: 'Wi-Fi' },
    { rotulo: 'Frigorífico', texto: 'frigorífico' },
    { rotulo: 'WC a bordo', texto: 'WC' },
    { rotulo: 'Ar condicionado', texto: 'ar condicionado' },
    { rotulo: 'TV', texto: 'TV' },
    { rotulo: 'USB', texto: 'USB' },
    { rotulo: 'Cama', texto: 'cama' },
    { rotulo: 'Cintos de 3 pontos', texto: 'cintos de 3 pontos' },
    { rotulo: 'Bagageira ampla', texto: 'bagageira ampla' },
    { rotulo: 'Microfone', texto: 'microfone' },
    { rotulo: 'Cadeira de rodas', texto: 'cadeira de rodas' },
    { rotulo: 'Dois pisos', texto: 'dois pisos' },
];
/** ⚠️ Não excluem viatura nenhuma — avisam se o condutor escolhido não tiver a competência. */
export const REQUISITOS_DO_CONDUTOR = [
    { rotulo: 'Condutor com inglês', texto: 'condutor com ingles' },
    { rotulo: 'Condutor com espanhol', texto: 'condutor com espanhol' },
    { rotulo: 'Condutor com francês', texto: 'condutor com frances' },
    { rotulo: 'Condutor com alemão', texto: 'alemão' },
];
export const REQUISITOS_SELECIONAVEIS = [
    ...REQUISITOS_DA_VIATURA,
    ...REQUISITOS_DO_CONDUTOR,
];
const TABELA = tabelaDeSinonimos(SINONIMOS_CONHECIDOS);
/** As chaves de sinónimo com mais de uma palavra, a mais longa primeiro. */
const FRASES_CHAVE = [...TABELA.keys()].filter((k) => k.includes(' ')).sort((a, b) => b.length - a.length);
const caracteristicaDe = (t) => normalizarRequisito(t, TABELA).caracteristica;
/** A característica de cada item da lista — é por ela que uma frase do email casa com um item. */
const ITEM_DA_CARACTERISTICA = new Map(REQUISITOS_SELECIONAVEIS.map((r) => [caracteristicaDe(r.texto), r]).filter(([k]) => k !== null));
/** O item da lista a que esta parte corresponde, ou `null`. */
function itemDe(parte) {
    const n = normalizarTexto(parte);
    const exacto = REQUISITOS_SELECIONAVEIS.find((r) => normalizarTexto(r.texto) === n || normalizarTexto(r.rotulo) === n);
    if (exacto !== undefined)
        return exacto;
    const directa = caracteristicaDe(parte);
    if (directa !== null)
        return ITEM_DA_CARACTERISTICA.get(directa) ?? null;
    const frase = FRASES_CHAVE.find((k) => ` ${n} `.includes(` ${k} `));
    const daFrase = frase === undefined ? null : (TABELA.get(frase)?.caracteristica ?? null);
    return daFrase === null ? null : (ITEM_DA_CARACTERISTICA.get(daFrase) ?? null);
}
/**
 * O texto de requisitos (do email, ou de um pedido antigo) partido em itens da LISTA e no que
 * fica fora dela. ⚠️ **Nada se deita fora:** o que não casa volta em `foraDaLista`, para quem
 * chama o mostrar (a ficha) ou o guardar nas notas (a gravação) — nunca como requisito.
 */
export function requisitosDaLista(texto) {
    const escolhidos = new Set();
    const foraDaLista = [];
    for (const parte of separarRequisitos(texto ?? '')) {
        const item = itemDe(parte);
        if (item === null)
            foraDaLista.push(parte);
        else
            escolhidos.add(item);
    }
    return { escolhidos: REQUISITOS_SELECIONAVEIS.filter((r) => escolhidos.has(r)), foraDaLista };
}
/** Os itens escolhidos no formato que `pedido.requisitos` guarda, ou `null` sem nenhum. */
export function textoDosItens(itens) {
    return itens.length === 0 ? null : itens.map((r) => r.texto).join(', ');
}
/**
 * 📗 2026-10-22 · **Numa ALTERAÇÃO os requisitos JUNTAM-SE** (decisão do cliente: a grelha abre com
 * os da reserva e os novos do email, e junta por omissão). «Acrescentamos uma cadeira de rodas»
 * não apaga o microfone que a reserva já pedia.
 *
 * ⚠️ O texto do plano que NÃO está na lista (pedidos anteriores à grelha) fica no fim, tal como
 * estava — a mesma regra do «Editar pedido» (`requisitosParaEdicao`): escrever só os itens da lista
 * apagava-o em silêncio.
 */
export function juntarRequisitos(plano, email) {
    const p = requisitosDaLista(plano);
    const e = requisitosDaLista(email);
    const doPlano = new Set(p.escolhidos);
    const novos = e.escolhidos.filter((r) => !doPlano.has(r));
    const uniao = REQUISITOS_SELECIONAVEIS.filter((r) => doPlano.has(r) || novos.includes(r));
    const partes = [textoDosItens(uniao), ...p.foraDaLista].filter((x) => x !== null && x !== '');
    return { texto: partes.length === 0 ? null : partes.join(', '), novos };
}
//# sourceMappingURL=requisitos-da-lista.js.map